import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GuildDirectory } from "@/components/GuildDirectory";
import { GuildInviteSearch } from "@/components/GuildInviteSearch";
import {
  Users, Plus, Shield, ArrowLeft, Crown, ScrollText, MessageSquare, AlertTriangle,
  Mail, UserPlus, UserMinus, Settings, ChevronUp, ChevronDown, Check, X, Inbox,
} from "lucide-react";

const POLICY_LABELS = { open: "Open — anyone can join", approval: "Approval — leaders review join requests", invite: "Invite-only — leaders must invite" };

/** Signed-in Guild directory, filtered and paginated on the server. */
export function GuildsPage() {
  const { user } = useAuth();
  const [openNew, setOpenNew] = useState(false);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="guilds-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Users className="h-4 w-4 text-cyan-300" />
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-cyan-300">Playden Guilds</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-white tracking-[0.05em]">Player Guilds</h1>
        </div>
        {user && (
          <Button onClick={() => setOpenNew(true)} data-testid="guild-open-create"
            className="bg-cyan-400 text-black hover:bg-cyan-300">
            <Plus className="h-4 w-4 mr-1" /> New Guild
          </Button>
        )}
      </div>

      {user && <MyInvitesPanel />}

      <GuildDirectory />

      <NewGuildDialog open={openNew} onOpenChange={setOpenNew} />
    </div>
  );
}

function MyInvitesPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["guild-invites-mine"],
    queryFn: async () => (await api.get("/guilds/mine/invites")).data,
    staleTime: 10_000,
  });
  const items = data?.items || [];
  const accept = useMutation({
    mutationFn: async (id) => (await api.post(`/guilds/invites/${id}/accept`)).data,
    onSuccess: () => {
      toast.success("Joined Guild");
      qc.invalidateQueries({ queryKey: ["guild-invites-mine"] });
      qc.invalidateQueries({ queryKey: ["guilds"] });
    },
    onError: (e) => toast.error(apiError(e, "Accept failed")),
  });
  const decline = useMutation({
    mutationFn: async (id) => (await api.post(`/guilds/invites/${id}/decline`)).data,
    onSuccess: () => { toast("Invite declined"); qc.invalidateQueries({ queryKey: ["guild-invites-mine"] }); },
    onError: (e) => toast.error(apiError(e, "Decline failed")),
  });
  if (items.length === 0) return null;
  return (
    <div className="mb-4 rounded-2xl border border-amber-300/30 bg-amber-400/[0.05] p-3" data-testid="my-guild-invites">
      <div className="flex items-center gap-2 mb-2">
        <Inbox className="h-4 w-4 text-amber-300" />
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-amber-300">Guild invites · {items.length}</span>
      </div>
      <div className="space-y-1.5">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between gap-2 rounded-lg bg-black/25 border border-white/10 px-2.5 py-1.5"
            data-testid={`my-invite-${it.id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-lg leading-none">{it.guild?.crest || "🛡"}</span>
              <div className="min-w-0">
                <div className="text-white text-sm truncate">{it.guild?.name || "Guild"}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 truncate">{it.note || it.guild?.motto || "—"}</div>
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="sm" onClick={() => accept.mutate(it.id)} disabled={accept.isPending}
                data-testid={`my-invite-${it.id}-accept`} className="h-7 px-2 text-[11px] bg-emerald-400 text-black hover:bg-emerald-300">
                <Check className="h-3 w-3 mr-1" /> Accept
              </Button>
              <Button size="sm" variant="outline" onClick={() => decline.mutate(it.id)} disabled={decline.isPending}
                data-testid={`my-invite-${it.id}-decline`} className="h-7 px-2 text-[11px] border-white/20 text-white/70 hover:bg-white/10">
                <X className="h-3 w-3 mr-1" /> Decline
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewGuildDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [motto, setMotto] = useState("");
  const [description, setDescription] = useState("");
  const [crest, setCrest] = useState("🛡");
  const [joinPolicy, setJoinPolicy] = useState("open");

  const create = useMutation({
    mutationFn: async () => (await api.post("/guilds", {
      name: name.trim(), motto: motto.trim() || null, description: description.trim() || null,
      crest: crest || null, join_policy: joinPolicy,
    })).data,
    onSuccess: (g) => {
      toast.success(`Founded ${g.name}`);
      qc.invalidateQueries({ queryKey: ["guilds"] });
      onOpenChange(false);
      setName(""); setMotto(""); setDescription("");
    },
    onError: (e) => toast.error(apiError(e, "Couldn't create Guild")),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[hsl(222_22%_7%)] border-white/10" data-testid="guild-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white"><Shield className="h-4 w-4 text-cyan-300" /> Found a Guild</DialogTitle>
          <DialogDescription className="text-white/55 text-xs">You become the founder automatically. Pick who can join and pledge the Guild to a House from the Guild Hall.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={48} placeholder="e.g. Ashen Wardens"
              data-testid="guild-create-name" className="mt-1 bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Motto</label>
            <Input value={motto} onChange={(e) => setMotto(e.target.value)} maxLength={120}
              data-testid="guild-create-motto" className="mt-1 bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Crest (emoji)</label>
            <Input value={crest} onChange={(e) => setCrest(e.target.value)} maxLength={4}
              data-testid="guild-create-crest" className="mt-1 bg-white/5 border-white/10 text-white w-20" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Description</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500}
              data-testid="guild-create-desc" className="mt-1 bg-white/5 border-white/10 text-white min-h-[60px]" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Join policy</label>
            <select value={joinPolicy} onChange={(e) => setJoinPolicy(e.target.value)}
              data-testid="guild-create-policy"
              className="mt-1 w-full h-9 rounded-md border border-white/10 bg-white/5 text-white text-sm px-2">
              <option value="open">Open — anyone can join</option>
              <option value="approval">Approval — leaders review join requests</option>
              <option value="invite">Invite-only — leaders must invite</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-white/70">Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending || !name.trim()}
            data-testid="guild-create-submit" className="bg-cyan-400 text-black hover:bg-cyan-300">
            {create.isPending ? "Founding…" : "Found Guild"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Guild Hall detail — roster, rules, meetings, House allegiance, join/leave. */
export function GuildDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { data: g, isLoading } = useQuery({
    queryKey: ["guild", id],
    queryFn: async () => (await api.get(`/guilds/${id}`)).data,
    staleTime: 10_000,
  });

  const join = useMutation({
    mutationFn: async () => (await api.post(`/guilds/${id}/join`)).data,
    onSuccess: (r) => {
      if (r.pending) toast("Join request sent — waiting for a leader to approve.", { icon: "⏳" });
      else toast.success("Joined.");
      qc.invalidateQueries({ queryKey: ["guild", id] });
      qc.invalidateQueries({ queryKey: ["guild-join-requests", id] });
    },
    onError: (e) => toast.error(apiError(e, "Join failed")),
  });
  const leave = useMutation({
    mutationFn: async () => (await api.post(`/guilds/${id}/leave`)).data,
    onSuccess: () => { toast.success("Left."); qc.invalidateQueries({ queryKey: ["guild", id] }); },
    onError: (e) => toast.error(apiError(e, "Leave failed")),
  });

  if (isLoading || !g) return <div className="max-w-4xl mx-auto px-4 py-10 text-white/50" data-testid="guild-detail-loading">Loading Guild…</div>;

  const myMember = (g.members || []).find((m) => m.user_id === user?.id);
  const isMember = !!myMember;
  const isFounder = g.founder_id === user?.id;
  const isOfficer = myMember?.role === "officer" || myMember?.role === "leader" || isFounder;
  const policy = g.join_policy || "open";

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid={`guild-detail-${g.id}`}>
      <Link to="/guilds" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.28em] text-white/45 hover:text-cyan-300 mb-3">
        <ArrowLeft className="h-3 w-3" /> All Guilds
      </Link>
      <div className="rounded-3xl border border-cyan-300/30 bg-cyan-400/[0.05] p-5">
        <div className="flex items-start gap-3">
          <div className="h-14 w-14 rounded-2xl bg-cyan-400/10 border border-cyan-300/30 flex items-center justify-center text-3xl">{g.crest || "🛡"}</div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-3xl text-white">{g.name}</h1>
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/40 bg-amber-400/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.24em] text-amber-300">
                <Crown className="h-3 w-3" /> House · {g.house_key || "unhoused"}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-black/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.24em] text-white/70" data-testid={`guild-policy-${g.id}`}>
                {policy}
              </span>
            </div>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.24em] text-white/55">{g.motto || "—"}</p>
            <p className="mt-2 text-[13px] text-white/70">{g.description || ""}</p>
          </div>
          <div className="flex flex-col gap-1.5 items-stretch">
            {user && (
              isMember ? (
                !isFounder && (
                  <Button onClick={() => leave.mutate()} disabled={leave.isPending} variant="outline"
                    data-testid="guild-leave-button" className="border-red-400/40 text-red-300 hover:bg-red-400/10">
                    Leave
                  </Button>
                )
              ) : policy === "invite" ? (
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50 self-end" data-testid="guild-invite-only-note">
                  Invite-only
                </span>
              ) : (
                <Button onClick={() => join.mutate()} disabled={join.isPending} data-testid="guild-join-button"
                  className="bg-cyan-400 text-black hover:bg-cyan-300">
                  {policy === "approval" ? "Request to join" : "Join"}
                </Button>
              )
            )}
            {isFounder && (
              <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)}
                data-testid={`guild-settings-${g.id}`} className="border-white/15 text-white/80 hover:bg-white/5">
                <Settings className="h-3 w-3 mr-1" /> Settings
              </Button>
            )}
          </div>
        </div>
        {isFounder && (
          <div className="mt-3 flex justify-end">
            <GuildPledgeButton guild={g} />
          </div>
        )}
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <Panel icon={Users} title={`Members · ${(g.members || []).length}`} testid="guild-roster">
          <div className="space-y-1">
            {(g.members || []).map((m) => (
              <MemberRow key={m.user_id} m={m} guild={g} isFounder={isFounder} isOfficer={isOfficer} myId={user?.id} />
            ))}
          </div>
        </Panel>

        <Panel icon={ScrollText} title={`Rulebook · ${(g.rules || []).length}`} testid="guild-rulebook">
          {(g.rules || []).length === 0 ? (
            <div className="text-white/45 text-[12px]">No rules yet. Members can propose one via a meeting.</div>
          ) : (
            <ol className="list-decimal pl-5 space-y-1 text-[12.5px] text-white/75">
              {g.rules.map((r) => (<li key={r.id}>{r.text}</li>))}
            </ol>
          )}
        </Panel>

        {isOfficer && (
          <Panel icon={UserPlus} title="Invite a player" testid="guild-invite-panel">
            <GuildInviteSearch key={g.id} guildId={g.id} />
          </Panel>
        )}

        {isOfficer && (
          <Panel icon={Mail} title="Pending join requests" testid="guild-join-requests-panel">
            <JoinRequestsList guildId={g.id} />
          </Panel>
        )}

        <Panel icon={MessageSquare} title={`Meetings · ${(g.meetings || []).length}`} testid="guild-meetings" className="md:col-span-2">
          {isMember && (
            <div className="mb-3">
              <MeetingCreator guildId={g.id} />
            </div>
          )}
          {(g.meetings || []).length === 0 ? (
            <div className="text-white/45 text-[12px]">No meetings on record yet.</div>
          ) : (
            <div className="space-y-2">
              {g.meetings.map((m) => (
                <MeetingRow key={m.id} m={m} guildId={g.id} isMember={isMember} isFounder={isFounder} userId={user?.id} />
              ))}
            </div>
          )}
        </Panel>
      </div>

      {isFounder && (
        <GuildSettingsDialog guild={g} open={settingsOpen} onOpenChange={setSettingsOpen} />
      )}
    </div>
  );
}

function MemberRow({ m, guild, isFounder, isOfficer, myId }) {
  const qc = useQueryClient();
  const isFounderRow = guild.founder_id === m.user_id;
  const roleColor = m.role === "leader" ? "#fbbf24" : m.role === "officer" ? "#38bdf8" : "#94a3b8";

  const setRole = useMutation({
    mutationFn: async (role) => (await api.post(`/guilds/${guild.id}/members/${m.user_id}/role`, { role })).data,
    onSuccess: () => { toast.success("Role updated"); qc.invalidateQueries({ queryKey: ["guild", guild.id] }); },
    onError: (e) => toast.error(apiError(e, "Couldn't change role")),
  });
  const kick = useMutation({
    mutationFn: async () => (await api.post(`/guilds/${guild.id}/members/${m.user_id}/kick`)).data,
    onSuccess: () => { toast.success("Member removed"); qc.invalidateQueries({ queryKey: ["guild", guild.id] }); },
    onError: (e) => toast.error(apiError(e, "Couldn't remove")),
  });

  return (
    <div className="flex items-center justify-between gap-2 text-[12px]" data-testid={`guild-member-${m.user_id}`}>
      <div className="min-w-0 flex-1">
        <div className="text-white/85 truncate">{m.name || m.user_id.slice(0, 8)}</div>
        <div className="font-mono text-[9px] uppercase tracking-[0.22em]" style={{ color: roleColor }}>
          {isFounderRow ? "founder" : (m.role || "member")}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        {isFounder && !isFounderRow && m.role !== "officer" && (
          <Button size="sm" onClick={() => setRole.mutate("officer")} disabled={setRole.isPending}
            data-testid={`guild-member-${m.user_id}-promote`} className="h-6 px-1.5 text-[10px] bg-cyan-400/20 text-cyan-200 hover:bg-cyan-400/30 border border-cyan-400/40">
            <ChevronUp className="h-3 w-3" />
          </Button>
        )}
        {isFounder && !isFounderRow && m.role === "officer" && (
          <Button size="sm" onClick={() => setRole.mutate("member")} disabled={setRole.isPending}
            data-testid={`guild-member-${m.user_id}-demote`} className="h-6 px-1.5 text-[10px] bg-white/10 text-white/70 hover:bg-white/20 border border-white/20">
            <ChevronDown className="h-3 w-3" />
          </Button>
        )}
        {isOfficer && !isFounderRow && m.user_id !== myId && (
          <Button size="sm" variant="outline" onClick={() => window.confirm(`Kick ${m.name || m.user_id.slice(0, 8)} from this Guild?`) && kick.mutate()}
            disabled={kick.isPending} data-testid={`guild-member-${m.user_id}-kick`}
            className="h-6 px-1.5 text-[10px] border-red-400/40 text-red-300 hover:bg-red-400/10">
            <UserMinus className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

function JoinRequestsList({ guildId }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["guild-join-requests", guildId],
    queryFn: async () => (await api.get(`/guilds/${guildId}/join-requests`)).data,
    staleTime: 10_000,
  });
  const items = data?.items || [];
  const approve = useMutation({
    mutationFn: async (rid) => (await api.post(`/guilds/${guildId}/join-requests/${rid}/approve`)).data,
    onSuccess: () => {
      toast.success("Approved");
      qc.invalidateQueries({ queryKey: ["guild-join-requests", guildId] });
      qc.invalidateQueries({ queryKey: ["guild", guildId] });
    },
    onError: (e) => toast.error(apiError(e, "Approve failed")),
  });
  const decline = useMutation({
    mutationFn: async (rid) => (await api.post(`/guilds/${guildId}/join-requests/${rid}/decline`)).data,
    onSuccess: () => { toast("Declined"); qc.invalidateQueries({ queryKey: ["guild-join-requests", guildId] }); },
    onError: (e) => toast.error(apiError(e, "Decline failed")),
  });
  if (items.length === 0) return <div className="text-white/45 text-[12px]">No pending requests.</div>;
  return (
    <div className="space-y-1.5">
      {items.map((r) => (
        <div key={r.id} className="flex items-center justify-between gap-2 rounded-lg bg-black/25 border border-white/10 px-2.5 py-1.5"
          data-testid={`guild-request-${r.id}`}>
          <div className="min-w-0 flex-1">
            <div className="text-white/85 text-[12px] truncate">{r.user?.name || r.user_id.slice(0, 8)}</div>
            {r.note && <div className="text-white/50 text-[11px] truncate">{r.note}</div>}
          </div>
          <div className="flex gap-1">
            <Button size="sm" onClick={() => approve.mutate(r.id)} disabled={approve.isPending}
              data-testid={`guild-request-${r.id}-approve`} className="h-6 px-1.5 text-[10px] bg-emerald-400 text-black hover:bg-emerald-300">
              <Check className="h-3 w-3" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => decline.mutate(r.id)} disabled={decline.isPending}
              data-testid={`guild-request-${r.id}-decline`} className="h-6 px-1.5 text-[10px] border-white/20 text-white/70 hover:bg-white/10">
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function GuildSettingsDialog({ guild, open, onOpenChange }) {
  const qc = useQueryClient();
  const [motto, setMotto] = useState(guild.motto || "");
  const [description, setDescription] = useState(guild.description || "");
  const [crest, setCrest] = useState(guild.crest || "");
  const [policy, setPolicy] = useState(guild.join_policy || "open");

  const save = useMutation({
    mutationFn: async () => (await api.patch(`/guilds/${guild.id}`, {
      motto: motto.trim(), description: description.trim(), crest: crest || null, join_policy: policy,
    })).data,
    onSuccess: () => {
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["guild", guild.id] });
      qc.invalidateQueries({ queryKey: ["guilds"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e, "Save failed")),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[hsl(222_22%_7%)] border-white/10" data-testid={`guild-settings-dialog-${guild.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white"><Settings className="h-4 w-4 text-cyan-300" /> Guild settings</DialogTitle>
          <DialogDescription className="text-white/55 text-xs">Only the founder can change these.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Motto</label>
            <Input value={motto} onChange={(e) => setMotto(e.target.value)} maxLength={120}
              data-testid={`guild-settings-${guild.id}-motto`} className="mt-1 bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Description</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000}
              data-testid={`guild-settings-${guild.id}-desc`} className="mt-1 bg-white/5 border-white/10 text-white min-h-[60px]" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Crest (emoji)</label>
            <Input value={crest} onChange={(e) => setCrest(e.target.value)} maxLength={4}
              data-testid={`guild-settings-${guild.id}-crest`} className="mt-1 bg-white/5 border-white/10 text-white w-20" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Join policy</label>
            <select value={policy} onChange={(e) => setPolicy(e.target.value)}
              data-testid={`guild-settings-${guild.id}-policy`}
              className="mt-1 w-full h-9 rounded-md border border-white/10 bg-white/5 text-white text-sm px-2">
              <option value="open">{POLICY_LABELS.open}</option>
              <option value="approval">{POLICY_LABELS.approval}</option>
              <option value="invite">{POLICY_LABELS.invite}</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-white/70">Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}
            data-testid={`guild-settings-${guild.id}-save`} className="bg-cyan-400 text-black hover:bg-cyan-300">
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Panel({ icon: Icon, title, children, testid, className = "" }) {
  return (
    <div className={`rounded-2xl border border-white/10 bg-black/30 p-3 ${className}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-cyan-300" />
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-cyan-300">{title}</span>
      </div>
      {children}
    </div>
  );
}

/** Founder-only button + dialog to pledge the Guild to a House. Mirrors the
 *  wipe-confirmation guard the card pledge uses on the backend. */
function GuildPledgeButton({ guild }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(guild.house_key || "unhoused");
  const [confirmed, setConfirmed] = useState(false);
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["houses"],
    queryFn: async () => (await api.get("/houses")).data,
    staleTime: 60_000,
    enabled: open,
  });
  const houses = data?.houses || [];
  const current = houses.find((h) => h.key === (guild.house_key || "unhoused")) || { key: "unhoused", name: "Unhoused" };
  const isChangingAway = current.key !== "unhoused" && target !== current.key;

  const pledge = useMutation({
    mutationFn: async () => (await api.post(`/guilds/${guild.id}/pledge`, {
      house_key: target, confirmed_wipe: isChangingAway ? confirmed : true,
    })).data,
    onSuccess: () => {
      toast.success("Guild banner set");
      qc.invalidateQueries({ queryKey: ["guild", guild.id] });
      qc.invalidateQueries({ queryKey: ["houses"] });
      qc.invalidateQueries({ queryKey: ["guilds"] });
      setOpen(false);
    },
    onError: (e) => toast.error(apiError(e, "Pledge failed")),
  });

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`guild-pledge-${guild.id}`}
        className="border-amber-400/40 text-amber-200 hover:bg-amber-400/10">
        <Crown className="h-3 w-3 mr-1" /> Pledge Guild to House
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[hsl(222_22%_7%)] border-white/10" data-testid={`guild-pledge-dialog-${guild.id}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white"><Crown className="h-4 w-4 text-amber-300" /> Pledge {guild.name}</DialogTitle>
            <DialogDescription className="text-white/55 text-xs">
              Guilds pledge to one House. Leaving a House wipes the Guild&apos;s contributions and starts you at zero in the new one.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto">
            {houses.map((h) => {
              const active = h.key === target;
              const tint = h.color || "#94a3b8";
              return (
                <button key={h.key} type="button" onClick={() => { setTarget(h.key); setConfirmed(false); }}
                  data-testid={`guild-pledge-${guild.id}-option-${h.key}`}
                  className="text-left rounded-lg border p-2 transition-colors"
                  style={{ borderColor: active ? tint : "rgba(255,255,255,0.1)", background: active ? `${tint}18` : "rgba(255,255,255,0.02)" }}>
                  <div className="flex items-center gap-2">
                    <span className="text-base">{h.crest || "🏛"}</span>
                    <div className="min-w-0">
                      <div className="text-white text-sm font-semibold truncate">{h.name}</div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/40 truncate">{h.motto}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {isChangingAway && (
            <div className="rounded-lg border border-red-400/40 bg-red-400/[0.07] px-3 py-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-300 mt-0.5 shrink-0" />
                <div className="text-[12px] text-red-100/90 leading-snug">
                  Leaving <strong>{current.name}</strong> wipes this Guild&apos;s contributions and achievements there. It cannot be undone.
                </div>
              </div>
              <label className="mt-2 flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-white/70">
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
                  className="accent-red-400" data-testid={`guild-pledge-${guild.id}-confirm-wipe`} />
                I understand.
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70">Cancel</Button>
            <Button onClick={() => pledge.mutate()} disabled={pledge.isPending || target === current.key || (isChangingAway && !confirmed)}
              data-testid={`guild-pledge-${guild.id}-confirm`} className="bg-amber-400 text-black hover:bg-amber-300">
              {pledge.isPending ? "Pledging…" : "Pledge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Inline meeting creator — any member can propose a new / amended / removed rule. */
function MeetingCreator({ guildId }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [proposal, setProposal] = useState("");
  const [kind, setKind] = useState("create");
  const qc = useQueryClient();
  const create = useMutation({
    mutationFn: async () => (await api.post(`/guilds/${guildId}/meetings`, {
      title: title.trim(), proposal: proposal.trim(), change_kind: kind,
    })).data,
    onSuccess: () => {
      toast.success("Meeting opened");
      qc.invalidateQueries({ queryKey: ["guild", guildId] });
      setOpen(false); setTitle(""); setProposal("");
    },
    onError: (e) => toast.error(apiError(e, "Couldn't open meeting")),
  });
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} data-testid={`guild-meeting-open-${guildId}`}
        className="bg-cyan-400 text-black hover:bg-cyan-300">
        <Plus className="h-3 w-3 mr-1" /> Propose a rule
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[hsl(222_22%_7%)] border-white/10" data-testid={`guild-meeting-create-${guildId}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white"><ScrollText className="h-4 w-4 text-cyan-300" /> Propose a rule change</DialogTitle>
            <DialogDescription className="text-white/55 text-xs">Members vote yea/nay. When the founder closes the meeting, a passing vote applies the rulebook change automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
                data-testid={`guild-meeting-title-${guildId}`} className="mt-1 bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Kind</label>
              <select value={kind} onChange={(e) => setKind(e.target.value)}
                data-testid={`guild-meeting-kind-${guildId}`}
                className="mt-1 w-full h-9 rounded-md border border-white/10 bg-white/5 text-white text-sm px-2">
                <option value="create">Create a new rule</option>
                <option value="amend">Amend an existing rule</option>
                <option value="remove">Remove an existing rule</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Proposed text</label>
              <Textarea value={proposal} onChange={(e) => setProposal(e.target.value)} maxLength={1000}
                data-testid={`guild-meeting-proposal-${guildId}`} className="mt-1 bg-white/5 border-white/10 text-white min-h-[80px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70">Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={create.isPending || !title.trim() || !proposal.trim()}
              data-testid={`guild-meeting-submit-${guildId}`} className="bg-cyan-400 text-black hover:bg-cyan-300">
              {create.isPending ? "Opening…" : "Open meeting"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Single meeting row with vote + founder close buttons. */
function MeetingRow({ m, guildId, isMember, isFounder, userId }) {
  const qc = useQueryClient();
  const alreadyVoted = (m.voted_by || []).includes(userId);
  const vote = useMutation({
    mutationFn: async (choice) => (await api.post(`/guilds/${guildId}/meetings/${m.id}/vote`, { choice })).data,
    onSuccess: () => { toast.success("Vote recorded"); qc.invalidateQueries({ queryKey: ["guild", guildId] }); },
    onError: (e) => toast.error(apiError(e, "Vote failed")),
  });
  const close = useMutation({
    mutationFn: async () => (await api.post(`/guilds/${guildId}/meetings/${m.id}/close`)).data,
    onSuccess: (r) => { toast.success(`Meeting ${r.result}`); qc.invalidateQueries({ queryKey: ["guild", guildId] }); },
    onError: (e) => toast.error(apiError(e, "Close failed")),
  });
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2" data-testid={`guild-meeting-${m.id}`}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-white text-sm truncate">{m.title}</div>
        <span className="font-mono text-[9px] uppercase tracking-[0.22em]" style={{ color: m.status === "open" ? "#38bdf8" : (m.result === "passed" ? "#a3e635" : "#f87171") }}>
          {m.status === "open" ? "open" : m.result}
        </span>
      </div>
      <div className="mt-1 text-[12px] text-white/60">{m.proposal}</div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
        yea {m.votes?.yea ?? 0} · nay {m.votes?.nay ?? 0} · abstain {m.votes?.abstain ?? 0}
        {m.change_kind && <span className="ml-2">· {m.change_kind}</span>}
      </div>
      {m.status === "open" && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {isMember && !alreadyVoted && (
            <>
              <Button size="sm" onClick={() => vote.mutate("yea")} disabled={vote.isPending}
                data-testid={`guild-meeting-vote-yea-${m.id}`}
                className="h-7 px-2 text-[11px] bg-emerald-400 text-black hover:bg-emerald-300">Yea</Button>
              <Button size="sm" onClick={() => vote.mutate("nay")} disabled={vote.isPending}
                data-testid={`guild-meeting-vote-nay-${m.id}`}
                className="h-7 px-2 text-[11px] bg-red-400 text-black hover:bg-red-300">Nay</Button>
              <Button size="sm" variant="outline" onClick={() => vote.mutate("abstain")} disabled={vote.isPending}
                data-testid={`guild-meeting-vote-abstain-${m.id}`}
                className="h-7 px-2 text-[11px] border-white/20 text-white/70 hover:bg-white/10">Abstain</Button>
            </>
          )}
          {alreadyVoted && <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">You voted</span>}
          {isFounder && (
            <Button size="sm" onClick={() => close.mutate()} disabled={close.isPending}
              data-testid={`guild-meeting-close-${m.id}`}
              className="h-7 px-2 text-[11px] bg-amber-400 text-black hover:bg-amber-300 ml-auto">
              Close &amp; apply
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
